import { restApi } from "../restApi";
import { DEFAULT_API_PAYLOAD } from "~/lib/constants";
import type { INotification } from "~/routes/portal/type";

const version = "1";

export interface NotificationsPage {
  data: {
    data: INotification[];
    pageIndex: number;
    pageCount: number;
    totalRecordCount: number;
  };
}

export interface UnreadCount {
  count: number;
}

export const notificationsApi = restApi.injectEndpoints({
  endpoints: (builder) => ({
    getNotifications: builder.query<
      NotificationsPage,
      { page?: number; pageSize?: number; unreadOnly?: boolean; "App-Name"?: string }
    >({
      query: (queryArg = {}) => ({
        url: `/api/v${version}/notifications`,
        params: {
          page: queryArg.page ?? 1,
          pageSize: queryArg.pageSize ?? 20,
          unreadOnly: queryArg.unreadOnly ?? false,
        },
        headers: {
          "App-Name": queryArg["App-Name"] || DEFAULT_API_PAYLOAD["App-Name"],
        },
      }),
      providesTags: ["Notifications" as any],
    }),
    getUnreadCount: builder.query<UnreadCount, { "App-Name"?: string } | void>({
      query: (queryArg) => ({
        url: `/api/v${version}/notifications/unread-count`,
        headers: {
          "App-Name": queryArg?.["App-Name"] || DEFAULT_API_PAYLOAD["App-Name"],
        },
      }),
      providesTags: ["Notifications" as any],
    }),
    markNotificationRead: builder.mutation<void, { id: string; "App-Name"?: string }>({
      query: (queryArg) => ({
        url: `/api/v${version}/notifications/${queryArg.id}/read`,
        method: "PUT",
        headers: {
          "App-Name": queryArg["App-Name"] || DEFAULT_API_PAYLOAD["App-Name"],
        },
      }),
      invalidatesTags: ["Notifications" as any],
    }),
    markAllNotificationsRead: builder.mutation<{ updated: number }, { "App-Name"?: string } | void>({
      query: (queryArg) => ({
        url: `/api/v${version}/notifications/read-all`,
        method: "PUT",
        headers: {
          "App-Name": queryArg?.["App-Name"] || DEFAULT_API_PAYLOAD["App-Name"],
        },
      }),
      invalidatesTags: ["Notifications" as any],
    }),
    markNotificationUnread: builder.mutation<void, { id: string; "App-Name"?: string }>({
      query: (queryArg) => ({
        url: `/api/v${version}/notifications/${queryArg.id}/unread`,
        method: "PUT",
        headers: {
          "App-Name": queryArg["App-Name"] || DEFAULT_API_PAYLOAD["App-Name"],
        },
      }),
      invalidatesTags: ["Notifications" as any],
    }),
    dismissNotification: builder.mutation<void, { id: string; "App-Name"?: string }>({
      query: (queryArg) => ({
        url: `/api/v${version}/notifications/${queryArg.id}`,
        method: "DELETE",
        headers: {
          "App-Name": queryArg["App-Name"] || DEFAULT_API_PAYLOAD["App-Name"],
        },
      }),
      invalidatesTags: ["Notifications" as any],
    }),
    getNotificationSettings: builder.query<any, { "App-Name"?: string } | void>({
      query: (queryArg) => ({
        url: `/api/v${version}/notification-settings`,
        headers: {
          "App-Name": queryArg?.["App-Name"] || DEFAULT_API_PAYLOAD["App-Name"],
        },
      }),
      providesTags: ["NotificationSettings" as any],
    }),
    updateNotificationSetting: builder.mutation<
      void,
      { notificationType: number; isEnabled: boolean; "App-Name"?: string }
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/notification-settings`,
        method: "PUT",
        body: {
          notificationType: queryArg.notificationType,
          isEnabled: queryArg.isEnabled,
        },
        headers: {
          "App-Name": queryArg["App-Name"] || DEFAULT_API_PAYLOAD["App-Name"],
        },
      }),
      invalidatesTags: ["NotificationSettings" as any],
    }),
  }),
});

export const {
  useGetNotificationsQuery,
  useGetUnreadCountQuery,
  useMarkNotificationReadMutation,
  useMarkAllNotificationsReadMutation,
  useMarkNotificationUnreadMutation,
  useDismissNotificationMutation,
  useGetNotificationSettingsQuery,
  useUpdateNotificationSettingMutation,
} = notificationsApi;
